import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import { useSiteInfo } from '../context/WorkingHoursContext';
import { SOCIAL_ICONS, socialHref } from '../config/socialIcons';
import WorkingHoursBanner from './WorkingHoursBanner';

const SOCIAL_KEYS = ['instagram', 'snapchat', 'tiktok', 'twitter'];

const Footer = () => {
  const { t, language } = useTranslation();
  const { contact, location } = useSiteInfo();
  const isRTL = language === 'ar';
  const year = new Date().getFullYear();

  const socials = SOCIAL_KEYS
    .map(key => ({ key, ...SOCIAL_ICONS[key], href: socialHref(key, contact[key]) }))
    .filter(s => s.href);

  const phoneLabel = contact.phoneDisplay || contact.phone;
  const address = location?.address?.[language];

  return (
    <footer className="bg-gradient-to-b from-gray-50 to-gray-100 border-t-2 border-blue-100 mt-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Working hours - same band as the top of the site */}
      <WorkingHoursBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 text-center sm:text-start">

          {/* Clinic identity */}
          <div>
            <div className="flex items-center justify-center sm:justify-start gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {t('footer.clinicName')}
              </h3>
            </div>
            <p className="text-sm text-gray-700 font-medium leading-relaxed mb-4">
              {t('footer.tagline')}
            </p>

            {socials.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t('footer.followUs')}
                </p>
                <div className="flex justify-center sm:justify-start gap-3">
                  {socials.map(social => (
                    <a
                      key={social.key}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      title={social.label}
                      className="p-2 bg-white rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 hover:shadow-md transition"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d={social.path} />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <h3 className="text-base font-extrabold text-gray-900 uppercase tracking-wide mb-4">
              {t('footer.address')}
            </h3>
            <ul className="space-y-3 text-sm text-gray-700 font-medium">
              {address && (
                <li className="flex items-start justify-center sm:justify-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{address}</span>
                </li>
              )}
              {location?.mapUrl && (
                <li>
                  <a
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition font-medium text-sm"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    {t('footer.directions')}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Contact + quick links */}
          <div>
            <h3 className="text-base font-extrabold text-gray-900 uppercase tracking-wide mb-4">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-2 text-sm">
              {contact.phone && (
                <li>
                  <a
                    href={`tel:+${contact.phone}`}
                    className="flex items-center justify-center sm:justify-start gap-2 text-gray-700 font-semibold hover:text-blue-600 transition"
                  >
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span dir="ltr">{phoneLabel}</span>
                  </a>
                </li>
              )}
              {contact.whatsapp && (
                <li>
                  <a
                    href={socialHref('whatsapp', contact.whatsapp)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center sm:justify-start gap-2 text-gray-700 font-semibold hover:text-green-600 transition"
                  >
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d={SOCIAL_ICONS.whatsapp.path} />
                    </svg>
                    <span>{t('footer.whatsapp')}</span>
                  </a>
                </li>
              )}
              {contact.email && (
                <li>
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center justify-center sm:justify-start gap-2 text-gray-700 font-semibold hover:text-blue-600 transition"
                  >
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span dir="ltr">{contact.email}</span>
                  </a>
                </li>
              )}
            </ul>

            {/* Quick links */}
            <div className="mt-6 pt-5 border-t border-gray-200">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                {t('footer.quickLinksTitle')}
              </p>
              <div className="flex flex-col gap-2">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition font-semibold shadow-md hover:shadow-lg text-sm justify-center"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t('footer.booking')}
                </Link>
                <Link
                  to="/prices"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition font-semibold text-sm justify-center"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  {t('footer.prices')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-center text-xs sm:text-sm text-gray-500">
            &copy; {year} {t('footer.clinicName')}. {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
